/* eslint-disable camelcase */
import axios from "axios";
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import actions from "./actions";
import {
  ACCOUNT_ACTIVATE,
  ACTIVATE_USER,
  CHECK,
  DOWNLOAD_FILE,
  FORGOT_PASSWORD,
  LOGIN,
  LOGOUT,
  REGISTER_ACCOUNT,
  RESET_PASSWORD,
  SENT_RESET_PASSWORD,
  UPDATE_PASSWORD,
  VERIFY_OTP,
  VERIFY_2FA,
  RESEND_2FA,
} from "./constants";
import { selectToken } from "./selectors";
const {
  checkFailure,
  checkSuccess,
  loginFailure,
  loginSuccess,
  logoutFailure,
  sentResetLinkSuccess,
  sentResetLinkFailure,
  activateAccountSuccess,
  activateAccountFailure,
  activateUserSuccess,
  activateUserFailure,
  updatePasswordSuccess,
  updatePasswordFailure,
  registerAccountSuccess,
  registerAccountFailure,
  downloadFileSuccess,
  downloadFileFailure,
  verifyOTPSuccess,
  verifyOTPFailure,
  forgotPasswordSuccess,
  forgotPasswordFailure,
  resetPasswordSuccess,
  resetPasswordFailure,
  verify2FASuccess,
  verify2FAFailure,
  resend2FASuccess,
  resend2FAFailure,
  show2FAScreen,
} = actions;

export function* checkAuth({ token, history }) {
  if (token) {
    try {
      const userInfo = yield call(api(token).post, `/api/me`);
      if (userInfo instanceof Error) throw userInfo;
      
      // Handle both encrypted and non-encrypted responses
      const userData = userInfo?.data?.data || userInfo?.data;

      yield put(checkSuccess({ token, user: userData }));
      if (history.location.pathname === "/")
        history.replace(
          history.location.state && history.location.state.referrer
            ? history.location.state.referrer.pathname
            : "/d/users"
        );
    } catch (e) {
      yield put(checkFailure(e));
      localStorage.clear();
      if (history.location.pathname !== "/") history.replace("/");
    }
  } else yield put(checkFailure("Invalid token"));
}
export function* checkAuthFlow() {
  yield takeLatest(CHECK, checkAuth);
}

export function* login({ credentials, history }) {
  try {
    // Enable interceptors for encryption/decryption
    const loginResponse = yield call(
      api(null, null, false).post,
      `/api/login`,
      credentials
    );

    const responseData = loginResponse?.data?.data || loginResponse?.data;

    if (responseData?.two_factor_required === true) {
      yield put(show2FAScreen({ 
        email: responseData.email,
        rememberMe: credentials.remember_me || false 
      }));
      yield put(successAlert(loginResponse?.message || 'Verification code sent to your email'));
      return;
    }

    const { 
      access_token, 
      expires_in, 
      expires_at, 
      refresh_ttl 
    } = responseData;

    if (access_token) {
      const userInfoResponse = yield call(
        api(access_token, null, false).post,
        `/api/me`
      );

      const userData = userInfoResponse?.data?.data || userInfoResponse?.data;
      
      yield put(
        loginSuccess({ token: access_token, user: userData })
      );
      
      // Store token with expiration info
      localStorage.setItem("token", access_token);
      if (expires_at) {
        localStorage.setItem("token_expires_at", expires_at);
      }
      if (expires_in) {
        localStorage.setItem("token_expires_in", expires_in);
      }
      
      const currentUserItem = userData?.role?.access_permissions?.find(
        (x) => x?.item_name === "JOB"
      );
      
      if (currentUserItem?.permission == "NO ACCESS") {
        history.push("/d");
      } else {
        history.push(
          history.location.state &&
            history.location.state.referrer &&
            history.location.state.referrer.pathname !== "/"
            ? history.location.state.referrer.pathname
            : "/d/company"
        );
      }
    } else if (history.location.pathname !== "/") history.replace("/");
  } catch (e) {
    yield put(loginFailure(e));
    yield put(errorAlert(e?.response?.data?.message || e?.error || "Login failed"));
    localStorage.clear();
    if (history.location.pathname !== "/") history.replace("/");
  }
}
export function* loginFlow() {
  yield takeLatest(LOGIN, login);
}

export function* logout({ history }) {
  try {
    const token = yield select(selectToken);
    yield call(api(token).post, `/api/logout`);
    localStorage.clear();
    // eslint-disable-next-line no-restricted-globals
    if (location) location.replace("/");
  } catch (e) {
    yield put(logoutFailure(e));
    localStorage.clear();
    // eslint-disable-next-line no-restricted-globals
    if (location) location.replace("/");
  }
}
export function* logoutFlow() {
  yield takeLatest(LOGOUT, logout);
}

export function* sentResetLink({ payload, history }) {
  try {
    const response = yield call(
      api(null, null, true).post,
      `/api/auth/password/email`,
      payload
    );
    const data = response.data;
    if (data) {
      successAlert(data.message);
      sentResetLinkSuccess();
      history.push(
        history.location.state &&
          history.location.state.referrer &&
          history.location.state.referrer.pathname !== "/login"
          ? history.location.state.referrer.pathname
          : "/dealers"
      );
    } else if (history.location.pathname !== "/login")
      history.replace("/login");
  } catch (e) {
    yield put(sentResetLinkFailure(e));
    yield put(errorAlert("Error while login!"));
    localStorage.clear();
    if (history.location.pathname !== "/login") history.replace("/login");
  }
}

export function* sentResetLinkFlow() {
  yield takeLatest(SENT_RESET_PASSWORD, sentResetLink);
}

export function* registerAccount({ payload }) {
  const token = yield select(selectToken);
  try {
    const response = yield call(
      api(null).post,
      `/api/company/two-factor-send-mail`,
      payload
    );
    if (response) {
      yield put(
        successAlert("Email Registered Successfully! Check Your Email For OTP")
      );
      yield put(registerAccountSuccess());
    }
  } catch (e) {
    yield put(registerAccountFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error while registering!"));
  }
}

export function* registerAccountFlow() {
  yield takeEvery(REGISTER_ACCOUNT, registerAccount);
}
export function* activateEmailAccount({ payload }) {
  let formdata = JSON.parse(JSON.stringify(payload));
  if (formdata?.trader_id) {
    delete formdata.trader_id;
  }
  if (formdata?.type) {
    delete formdata.type;
  }
  try {
    const token = yield select(selectToken);
    const { data } = yield call(
      api(token).post,
      `/api/auth/one-time-invitation/email`,
      formdata
    );
    if (data) {
      yield put(successAlert("Verification Link Sent Successfully!"));
      yield put(activateAccountSuccess());
    }
  } catch (e) {
    yield put(activateAccountFailure());

    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error while activating account!"));
  }
}

export function* activateEmailFlow() {
  yield takeEvery(ACCOUNT_ACTIVATE, activateEmailAccount);
}

export function* activateUserAccount({ payload }) {
  const token = yield select(selectToken);
  let formdata = JSON.parse(JSON.stringify(payload));
  if (formdata?.trader_id) {
    delete formdata.trader_id;
  }
  if (formdata?.type) {
    delete formdata.type;
  }
  try {
    const response = yield call(
      api(token).post,
      `/api/auth/user/change-status`,
      formdata
    );
    if (response) {
      yield put(successAlert(response.message));
      yield put(activateUserSuccess());
    }
  } catch (e) {
    yield put(activateUserFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error while activating account!"));
  }
}

export function* activateUserFlow() {
  yield takeEvery(ACTIVATE_USER, activateUserAccount);
}

export function* updateUsersPassword({ payload, history }) {
  try {
    const token = yield select(selectToken);
    const payloadData = payload.values;
    const response = yield call(
      api(token).post,
      `/api/auth/one-time-sign-up/user`,
      payload
    );
    // const history = payload.history;
    if (response) {
      yield put(successAlert(response.message));
      yield put(updatePasswordSuccess());
      if (payload.role == "contractor") {
        response.message !== "User is already verified" &&
          history.location.pathname !== "/login" &&
          history.push("/login");
      } else {
        response.message !== "User is already verified" &&
          history.push("/mobile");
      }
    }
  } catch (e) {
    yield put(updatePasswordFailure());
    // yield put(errorAlert('Error while updating password'));
  }
}

export function* updatePasswordFlow() {
  yield takeEvery(UPDATE_PASSWORD, updateUsersPassword);
}
export function* verifyOTP({ payload }) {
  //const token = yield select(selectToken);

  try {
    const response = yield call(
      api(null).post,
      `/api/company/verify-otp`,
      payload
    );
    if (response) {
      yield put(verifyOTPSuccess("OTP Verified Successfully!"));
    }
  } catch (e) {
    yield put(verifyOTPFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("OTP Verification Unsuccessfull!"));
  }
}
export function* verifyOTPFlow() {
  yield takeEvery(VERIFY_OTP, verifyOTP);
}

export function* downloadFile({ payload }) {
  axios({
    url: `${process.env.REACT_APP_BACKEND_URI}/api/company/download-file`,
    method: "POST",
    data: payload,
    responseType: "blob", // important
  })
    .then((response) => {
      if (response) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "insurancecompany");
        document.body.appendChild(link);
        link.click();
        downloadFileSuccess("File Download Started!");
      }
    })
    .catch((e) => {
      downloadFileFailure();
      if (typeof e === "string") errorAlert(e);
      else if (typeof e.error === "string") errorAlert(e.error);
      else errorAlert("Eror while downloading file!");
    });
}
export function* downloadFileFlow() {
  yield takeEvery(DOWNLOAD_FILE, downloadFile);
}

export function* forgotPassword({ credentials, history }) {
  const token = yield select(selectToken);
  try {
    const response = yield call(
      api(token).post,
      `/api/auth/password/email`,
      credentials
    );
    if (response && response?.code == 200) {
      yield put(forgotPasswordSuccess());
      yield put(successAlert(response.message));
    }
  } catch (e) {
    yield put(forgotPasswordFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error while sending reset link!"));
  }
}

export function* resetPassword({ credentials, history }) {
  const token = yield select(selectToken);
  try {
    const response = yield call(
      api(token).post,
      `/api/auth/password/reset`,
      credentials
    );
    if (response && response?.code == 200) {
      yield put(resetPasswordSuccess());
      yield put(successAlert(response.message));
    }
  } catch (e) {
    yield put(resetPasswordFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error while sending reset link!"));
  }
}

export function* forgotPasswordFlow() {
  yield takeEvery(FORGOT_PASSWORD, forgotPassword);
}

export function* resetPasswordFlow() {
  yield takeEvery(RESET_PASSWORD, resetPassword);
}

// Two Factor Authentication
export function* verify2FA({ payload }) {
  try {
    const response = yield call(
      api(null, null, false).post,
      `/api/verify-2fa`,
      payload
    );
    
    const responseData = response?.data?.data || response?.data;
    const { access_token, expires_in, expires_at } = responseData;

    if (access_token) {
      // Get user info
      const userInfoResponse = yield call(
        api(access_token, null, false).post,
        `/api/me`
      );
      
      yield put(
        verify2FASuccess({ 
          token: access_token, 
          user: userInfoResponse.data.data 
        })
      );
      yield put(
        loginSuccess({ 
          token: access_token, 
          user: userInfoResponse.data.data 
        })
      );
      
      // Store token with expiration info
      localStorage.setItem("token", access_token);
      if (expires_at) {
        localStorage.setItem("token_expires_at", expires_at);
      }
      if (expires_in) {
        localStorage.setItem("token_expires_in", expires_in);
      }
      
      yield put(successAlert("Login successful!"));
      
      // Redirect to dashboard
      window.location.href = "/d/company";
    }
  } catch (e) {
    yield put(verify2FAFailure(e));
    yield put(errorAlert(e?.response?.data?.message || e?.error || "Invalid verification code"));
  }
}

export function* verify2FAFlow() {
  yield takeLatest(VERIFY_2FA, verify2FA);
}

export function* resend2FA({ payload }) {
  try {
    const response = yield call(
      api(null, null, false).post,
      `/api/resend-2fa`,
      payload
    );
    
    yield put(resend2FASuccess());
    yield put(successAlert(response?.message || "Verification code resent to your email"));
  } catch (e) {
    yield put(resend2FAFailure(e));
    yield put(errorAlert(e?.response?.data?.message || e?.error || "Failed to resend code"));
  }
}

export function* resend2FAFlow() {
  yield takeLatest(RESEND_2FA, resend2FA);
}

export default function* rootAuthSaga() {
  yield all([
    loginFlow(),
    checkAuthFlow(),
    logoutFlow(),
    registerAccountFlow(),
    verifyOTPFlow(),
    downloadFileFlow(),
    sentResetLinkFlow(),
    activateEmailFlow(),
    updatePasswordFlow(),
    activateUserFlow(),
    forgotPasswordFlow(),
    resetPasswordFlow(),
    verify2FAFlow(),
    resend2FAFlow(),
  ]);
}
